import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { CalendarEvent } from './useSupabaseEvents';

// Legacy URL for single-cal entries created before multi-cal support
const LEGACY_GCAL_URL = 'google-calendar-sync';

// Auto-sync interval options (in minutes, 0 = manual only)
export type AutoSyncInterval = 0 | 5 | 15 | 30;
const AUTO_SYNC_KEY = 'mozayc-gcal-auto-sync-interval';

// Per-calendar sync token key in localStorage
const syncTokenKey = (importedCalId: string) => `gcal-sync-token:${importedCalId}`;

export interface GoogleCalendar {
  id: string;
  summary: string;
  primary: boolean;
}

export interface SyncedGoogleCalendar {
  id: string;          // imported_calendars.id
  googleCalendarId: string; // the Google Calendar ID (e.g. "primary" or email)
  name: string;
  color: string;
  lastSyncedAt: string | null;
}

/** Derive Google Calendar ID from imported_calendars URL */
export function googleCalIdFromUrl(url: string): string | null {
  if (url.startsWith('google-calendar:')) return url.slice('google-calendar:'.length);
  if (url === LEGACY_GCAL_URL) return 'primary';
  return null;
}

/** True if an imported_calendars URL is a Google Calendar (new or legacy format) */
export function isGoogleCalendarUrl(url: string | undefined | null): boolean {
  return !!url && googleCalIdFromUrl(url) !== null;
}

async function invokeSync(action: string, payload: Record<string, any> = {}) {
  const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
    body: { action, ...payload },
  });
  if (error) {
    // FunctionsHttpError only carries a generic message; the real cause is in the
    // response body. Read it so callers/Sentry see the actual edge-function error.
    let message = error.message;
    const res = (error as any).context;
    if (res && typeof res.json === 'function') {
      try {
        const body = await res.json();
        if (body?.error) message = body.error;
      } catch { /* body not JSON — keep generic message */ }
    }
    throw new Error(message);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

interface OptimisticCallbacks {
  addEvent: (event: any) => void;
  updateEvent: (match: { external_uid: string }, updates: any) => void;
}

export function useGoogleCalendarSync(
  onCalendarsChanged?: () => void,
  onEventsChanged?: () => void,
  optimistic?: OptimisticCallbacks,
) {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [syncedCalendars, setSyncedCalendars] = useState<SyncedGoogleCalendar[]>([]);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const configLoaded = useRef(false);

  // Auto-sync interval (persisted in localStorage)
  const [autoSyncInterval, setAutoSyncIntervalState] = useState<AutoSyncInterval>(() => {
    const stored = localStorage.getItem(AUTO_SYNC_KEY);
    return stored ? (Number(stored) as AutoSyncInterval) : 0;
  });
  const setAutoSyncInterval = useCallback((interval: AutoSyncInterval) => {
    setAutoSyncIntervalState(interval);
    localStorage.setItem(AUTO_SYNC_KEY, String(interval));
  }, []);

  /** Load synced calendars from imported_calendars */
  const loadSyncedCalendars = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('imported_calendars')
      .select('id, name, url, color, last_synced_at')
      .eq('user_id', user.id);

    const synced: SyncedGoogleCalendar[] = (data || [])
      .map((c: any) => {
        const googleCalendarId = googleCalIdFromUrl(c.url);
        if (!googleCalendarId) return null;
        return {
          id: c.id,
          googleCalendarId,
          name: c.name,
          color: c.color,
          lastSyncedAt: c.last_synced_at,
        };
      })
      .filter(Boolean) as SyncedGoogleCalendar[];

    setSyncedCalendars(synced);
    if (synced.length > 0) {
      const latest = synced.reduce((a, b) =>
        (a.lastSyncedAt || '') > (b.lastSyncedAt || '') ? a : b
      );
      setLastSyncedAt(latest.lastSyncedAt);
    }
  }, [user]);

  // Load connection status and synced calendars on mount
  useEffect(() => {
    if (!user || configLoaded.current) return;
    configLoaded.current = true;

    (async () => {
      // Check if user has connected Google Calendar (has refresh token)
      const { data: config } = await supabase
        .from('google_sync_config')
        .select('user_id')
        .eq('user_id', user.id)
        .single();

      if (config) setConnected(true);
      await loadSyncedCalendars();
    })();
  }, [user, loadSyncedCalendars]);

  /** Connect selected Google Calendars: creates imported_calendars entries */
  const connectCalendars = useCallback(async (gcals: GoogleCalendar[]) => {
    if (!user) return;

    for (const gcal of gcals) {
      const url = `google-calendar:${gcal.id}`;

      // Only insert if not already present
      const { data: existing } = await supabase
        .from('imported_calendars')
        .select('id')
        .eq('user_id', user.id)
        .eq('url', url)
        .single();

      if (!existing) {
        await supabase.from('imported_calendars').insert({
          user_id: user.id,
          name: gcal.summary,
          url,
          color: 'bg-blue-500',
          enabled: true,
        });
      }
    }

    await loadSyncedCalendars();
    onCalendarsChanged?.();
  }, [user, loadSyncedCalendars, onCalendarsChanged]);

  /** Disconnect a Google Calendar: removes imported_calendars entry + clears sync token */
  const disconnectCalendar = useCallback(async (googleCalId: string) => {
    if (!user) return;

    const url = googleCalId === 'primary' ? LEGACY_GCAL_URL : `google-calendar:${googleCalId}`;
    const legacyUrl = LEGACY_GCAL_URL;

    // Find the entry (support both new and legacy URL formats)
    const { data: entry } = await supabase
      .from('imported_calendars')
      .select('id')
      .eq('user_id', user.id)
      .in('url', [url, legacyUrl])
      .single();

    if (entry) {
      localStorage.removeItem(syncTokenKey(entry.id));
      await supabase.from('imported_calendars').delete().eq('id', entry.id);
    }

    await loadSyncedCalendars();
    onCalendarsChanged?.();
    onEventsChanged?.();
  }, [user, loadSyncedCalendars, onCalendarsChanged, onEventsChanged]);

  /** Fully disconnect Google Calendar: flips UI immediately, cleans up in background */
  const disconnectGoogle = useCallback((onError?: (msg: string) => void) => {
    if (!user) return;

    // Flip UI instantly — user sees the disconnected state before any network call.
    setConnected(false);
    setSyncedCalendars([]);
    setLastSyncedAt(null);
    onCalendarsChanged?.();
    onEventsChanged?.();

    // Background cleanup — delete config + local imported calendars.
    (async () => {
      try {
        await invokeSync('disconnect');

        const { data: entries } = await supabase
          .from('imported_calendars')
          .select('id, url')
          .eq('user_id', user.id);

        const googleEntries = (entries || []).filter((c: any) => googleCalIdFromUrl(c.url));
        for (const entry of googleEntries) {
          localStorage.removeItem(syncTokenKey(entry.id));
        }
        if (googleEntries.length > 0) {
          await supabase
            .from('imported_calendars')
            .delete()
            .in('id', googleEntries.map((e: any) => e.id));
        }

        onEventsChanged?.();
      } catch (e) {
        // Revert optimistic state so the user can try again
        setConnected(true);
        onCalendarsChanged?.();
        onError?.(e instanceof Error ? e.message : 'Failed to disconnect');
      }
    })();
  }, [user, onCalendarsChanged, onEventsChanged]);

  /** Pull changes for a single Google Calendar */
  const pullCalendar = useCallback(async (googleCalId: string, importedCalId: string) => {
    if (!user) return;

    const storedSyncToken = localStorage.getItem(syncTokenKey(importedCalId));

    const data = await invokeSync('pull-changes', {
      calendarId: googleCalId,
      syncToken: storedSyncToken || undefined,
    });

    // Store the new sync token for this calendar
    if (data.nextSyncToken) {
      localStorage.setItem(syncTokenKey(importedCalId), data.nextSyncToken);
    }

    // Upsert creates and updates
    const allEvents = [...(data.toCreate || []), ...(data.toUpdate || [])];
    if (allEvents.length > 0) {
      const googleIds = allEvents.map((e: any) => e.googleEventId);
      await supabase
        .from('imported_events')
        .delete()
        .eq('calendar_id', importedCalId)
        .in('external_uid', googleIds);

      const rows = allEvents.map((e: any) => ({
        user_id: user.id,
        calendar_id: importedCalId,
        external_uid: e.googleEventId,
        title: e.title,
        description: e.notes || '',
        dates: e.dates,
        all_day: e.all_day,
        start_time: e.start_time,
        end_time: e.end_time,
      }));
      await supabase.from('imported_events').insert(rows);
    }

    // Process deletes
    for (const googleEventId of data.toDelete || []) {
      await supabase
        .from('imported_events')
        .delete()
        .eq('calendar_id', importedCalId)
        .eq('external_uid', googleEventId);
    }

    // Update last_synced_at on the imported_calendars entry
    await supabase
      .from('imported_calendars')
      .update({ last_synced_at: new Date().toISOString(), sync_error: null })
      .eq('id', importedCalId);
  }, [user]);

  /** Pull changes for all synced Google Calendars */
  const pullChanges = useCallback(async () => {
    if (!connected || !user) return;
    try {
      setSyncing(true);

      // Re-read from state (may have been updated since last render)
      const { data: calEntries } = await supabase
        .from('imported_calendars')
        .select('id, url')
        .eq('user_id', user.id);

      const gcalEntries = (calEntries || []).filter((c: any) => googleCalIdFromUrl(c.url));
      if (gcalEntries.length === 0) return;

      // Settle independently so one failing calendar doesn't block the rest.
      const results = await Promise.allSettled(
        gcalEntries.map((c: any) => pullCalendar(googleCalIdFromUrl(c.url)!, c.id))
      );

      // Record per-calendar sync errors (surfaced in the sidebar) and keep a
      // representative error to re-throw for user-initiated syncs.
      let firstError: Error | null = null;
      await Promise.all(results.map(async (r, i) => {
        if (r.status === 'rejected') {
          const message = r.reason instanceof Error ? r.reason.message : String(r.reason);
          if (!firstError) firstError = new Error(message);
          await supabase
            .from('imported_calendars')
            .update({ sync_error: message })
            .eq('id', gcalEntries[i].id);
        }
      }));

      await loadSyncedCalendars();
      onCalendarsChanged?.();
      onEventsChanged?.();

      if (firstError) throw firstError;
    } catch (e) {
      console.error('Google Calendar pull failed:', e);
      throw e;
    } finally {
      setSyncing(false);
    }
  }, [connected, user, pullCalendar, loadSyncedCalendars, onCalendarsChanged, onEventsChanged]);

  const listCalendars = useCallback(async (): Promise<GoogleCalendar[]> => {
    if (!connected) return [];
    const data = await invokeSync('list-calendars');
    return data.calendars || [];
  }, [connected]);

  const pushEvent = useCallback(
    async (event: CalendarEvent, targetCalendarId?: string, googleEventId?: string) => {
      if (!connected) return;
      try {
        await invokeSync('push-event', {
          event: {
            title: event.title,
            notes: event.notes,
            dates: event.dates,
            all_day: event.all_day,
            start_time: event.start_time,
            end_time: event.end_time,
          },
          mozaycEventId: googleEventId ? undefined : event.id,
          googleEventId,
          targetCalendarId,
        });
      } catch (e) {
        console.error('Google Calendar push failed:', e);
      }
    },
    [connected]
  );

  /** Create event on Google Calendar with instant optimistic UI update */
  const createGoogleEvent = useCallback(
    (eventData: { title: string; notes: string; dates: string[]; all_day: boolean; start_time: string | null; end_time: string | null }) => {
      if (!connected || !user) return;

      // Use the primary calendar's imported_calendars entry
      const primaryCal = syncedCalendars.find(c => c.googleCalendarId === 'primary')
        ?? syncedCalendars[0];
      const calendarId = primaryCal?.id ?? null;

      const tempUid = `temp-${Date.now()}`;

      // Optimistic: add to local state instantly
      if (calendarId && optimistic) {
        optimistic.addEvent({
          id: tempUid,
          calendar_id: calendarId,
          external_uid: tempUid,
          title: eventData.title,
          description: eventData.notes || '',
          dates: eventData.dates,
          all_day: eventData.all_day,
          start_time: eventData.start_time,
          end_time: eventData.end_time,
        });
      }

      // Background: persist to DB + push to Google
      (async () => {
        try {
          if (!calendarId) return;

          const { data: inserted } = await supabase.from('imported_events').insert({
            user_id: user.id,
            calendar_id: calendarId,
            external_uid: tempUid,
            title: eventData.title,
            description: eventData.notes || '',
            dates: eventData.dates,
            all_day: eventData.all_day,
            start_time: eventData.start_time,
            end_time: eventData.end_time,
          }).select('id').single();

          if (inserted && optimistic) {
            optimistic.updateEvent({ external_uid: tempUid }, { id: inserted.id });
          }

          const result = await invokeSync('push-event', {
            event: eventData,
            targetCalendarId: primaryCal?.googleCalendarId ?? 'primary',
          });
          if (result?.googleEventId) {
            await supabase
              .from('imported_events')
              .update({ external_uid: result.googleEventId })
              .eq('calendar_id', calendarId)
              .eq('external_uid', tempUid);
          }
        } catch (e) {
          console.error('Google Calendar create failed:', e);
        }
      })();
    },
    [connected, user, syncedCalendars, optimistic]
  );

  const deleteGoogleEvent = useCallback(
    async (mozaycEventId: number | string) => {
      if (!connected) return;
      try {
        await invokeSync('delete-event', { mozaycEventId });
      } catch (e) {
        console.error('Google Calendar delete failed:', e);
      }
    },
    [connected]
  );

  const deleteGoogleEventByGoogleId = useCallback(
    (googleEventId: string, targetCalendarId?: string) => {
      if (!connected) return;
      invokeSync('delete-event', { googleEventId, targetCalendarId }).catch((e) => {
        console.error('Google Calendar delete failed:', e);
      });
    },
    [connected]
  );

  // Legacy: enableSync / disableSync kept for backward compat (SettingsPage, OnboardingWizard)
  const enableSync = useCallback(
    async (googleCalendarId = 'primary') => {
      if (!user) return;
      await supabase
        .from('google_sync_config')
        .update({ sync_enabled: true, google_calendar_id: googleCalendarId, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
    },
    [user]
  );

  const disableSync = useCallback(async () => {
    if (!user) return;
    await supabase
      .from('google_sync_config')
      .update({ sync_enabled: false, updated_at: new Date().toISOString() })
      .eq('user_id', user.id);
  }, [user]);

  // Auto-sync at configured interval
  useEffect(() => {
    if (!connected || syncedCalendars.length === 0 || autoSyncInterval === 0) return;
    const interval = setInterval(() => { pullChanges().catch(() => {}); }, autoSyncInterval * 60 * 1000);
    return () => clearInterval(interval);
  }, [connected, syncedCalendars.length, autoSyncInterval, pullChanges]);

  return {
    connected,
    syncEnabled: syncedCalendars.length > 0,
    syncedCalendars,
    lastSyncedAt,
    syncing,
    pushEvent,
    createGoogleEvent,
    deleteGoogleEvent,
    deleteGoogleEventByGoogleId,
    pullChanges,
    pullCalendar,
    listCalendars,
    connectCalendars,
    disconnectCalendar,
    disconnectGoogle,
    // Legacy compat
    enableSync,
    disableSync,
    autoSyncInterval,
    setAutoSyncInterval,
  };
}
